import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "reactstrap";
import { API_BASE } from "../../constants/api";

const GET_TENANT_BY_ID = gql` 
query getTenantById ($id:ID!) {
  getTenant(_id:$id){
   employeeId 
    roleId
    locationId
    permissionIds
    serviceIds
    locationIds
    firstName
    isAdmin
    adminId
    lastName
    email
    phoneNumber
    profilePicture
    permanentPasswordSet
    password
    tokenCount
    status
    company
    domain
  }
}
`

const GET_SMS_USAGE_DETAILS = gql`
  query getSmsUsageDetails($tenantId: String!, $fromDay: String, $toDay: String) {
    getSmsUsageDetails(tenantId: $tenantId, fromDay: $fromDay, toDay: $toDay) {
      tenantId
      fromDay
      toDay
      totalOutboundSent
      totalInboundReceived
      daily {
        day
        outboundSent
        inboundReceived
      }
    }
  }
`;

const GET_SMS_WALLET_BALANCE = gql`
  query getSmsWalletBalance($tenantId: String!) {
    getSmsWalletBalance(tenantId: $tenantId) {
      tenantId
      balanceUnits
    }
  }
`;

const GET_USAGE_DETAILS = gql`
  query getUsageDetails($tenantId: String!, $meter: String!, $fromDay: String, $toDay: String) {
    getUsageDetails(tenantId: $tenantId, meter: $meter, fromDay: $fromDay, toDay: $toDay) {
      tenantId
      meter
      fromDay
      toDay
      totalOutbound
      totalInbound
      daily {
        day
        outboundUnits
        inboundUnits
      }
    }
  }
`;

const toUtcDay = (date = new Date()) => date.toISOString().slice(0, 10);
const addDaysUtc = (date, days) =>
  new Date(date.getTime() + days * 24 * 60 * 60 * 1000);


const TenantDetails = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const [tenantDetails, setTenantDetails] = useState('')

  const handleNavigateSupport = () => {
    navigate("/tenants");
  };
  const { loading, error, data } = useQuery(GET_TENANT_BY_ID, {
    variables: { id: id },
    fetchPolicy: "network-only",
  });

  const usageTenantId = tenantDetails?.adminId || id;
  const toDay = toUtcDay();
  const fromDay = toUtcDay(addDaysUtc(new Date(), -29));

  const {
    loading: usageLoading,
    error: usageError,
    data: usageData,
    refetch: refetchUsage,
  } = useQuery(GET_SMS_USAGE_DETAILS, {
    variables: { tenantId: usageTenantId, fromDay, toDay },
    skip: !usageTenantId,
    fetchPolicy: "network-only",
  });

  const usage = usageData?.getSmsUsageDetails;

  const {
    loading: emailUsageLoading,
    error: emailUsageError,
    data: emailUsageData,
    refetch: refetchEmailUsage,
  } = useQuery(GET_USAGE_DETAILS, {
    variables: { tenantId: usageTenantId, meter: "email", fromDay, toDay },
    skip: !usageTenantId,
    fetchPolicy: "network-only",
  });

  const emailUsage = emailUsageData?.getUsageDetails;

  const {
    loading: walletLoading,
    error: walletError,
    data: walletData,
    refetch: refetchWallet,
  } = useQuery(GET_SMS_WALLET_BALANCE, {
    variables: { tenantId: usageTenantId },
    skip: !usageTenantId,
    fetchPolicy: "network-only",
  });

  const balanceUnits = walletData?.getSmsWalletBalance?.balanceUnits ?? 0;

  const [stripeSummary, setStripeSummary] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [meterData, setMeterData] = useState(null);
  const [meterLoading, setMeterLoading] = useState(false);
  const [meterError, setMeterError] = useState(null);

  const fetchStripeSummary = async () => {
    if (!usageTenantId) return;
    setStripeLoading(true);
    setStripeError(null);
    try {
      const resp = await fetch(
        `${API_BASE}/admin/stripe/customer-summary/${encodeURIComponent(usageTenantId)}`,
        { credentials: "include" }
      );
      const json = await resp.json();
      if (!resp.ok || json?.success === false) {
        throw new Error(json?.error || "Failed to load Stripe summary");
      }
      setStripeSummary(json);
    } catch (e) {
      setStripeError(e.message);
      setStripeSummary(null);
    } finally {
      setStripeLoading(false);
    }
  };

  const [stripeReport, setStripeReport] = useState(null);
  const [stripeReportLoading, setStripeReportLoading] = useState(false);
  const [stripeReportError, setStripeReportError] = useState(null);

  const fetchStripeReport = async () => {
    if (!usageTenantId) return;
    setStripeReportLoading(true);
    setStripeReportError(null);
    try {
      const resp = await fetch(
        `${API_BASE}/admin/stripe/tenant-report/${encodeURIComponent(usageTenantId)}?limit=25`,
        { credentials: "include" }
      );
      const json = await resp.json();
      if (!resp.ok || json?.success === false) {
        throw new Error(json?.error || "Failed to load Stripe tenant report");
      }
      setStripeReport(json);
    } catch (e) {
      setStripeReportError(e.message);
      setStripeReport(null);
    } finally {
      setStripeReportLoading(false);
    }
  };

  const fetchStripeMeterEvents = async () => {
    if (!usageTenantId) return;
    setMeterLoading(true);
    setMeterError(null);
    try {
      const resp = await fetch(
        `${API_BASE}/admin/stripe/meter-events/${encodeURIComponent(usageTenantId)}?limit=50`,
        { credentials: "include" }
      );
      const json = await resp.json();
      if (!resp.ok || json?.success === false) {
        throw new Error(json?.error || "Failed to load Stripe meter events");
      }
      setMeterData(json);
    } catch (e) {
      setMeterError(e.message);
      setMeterData(null);
    } finally {
      setMeterLoading(false);
    }
  };


  useEffect(() => {
    if (data && data.getTenant) {
      setTenantDetails(data.getTenant)
    }
    console.log(data, "data------>")
  }, [data])

  useEffect(() => {
    fetchStripeSummary();
    fetchStripeReport();
    fetchStripeMeterEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usageTenantId]);



  return (
    <div className="m-4">
      <div>

        <div className="card custom-card ">
          <div className="card-body">
          <h5
            className="card-title fw-bold fs-4 mb-4"
            style={{ fontFamily: "sans-serif" }}
          >
            Tenants Details
          </h5>
            <div className="p-4 mt-3">
              <div className="row">
                <div className="col-md-6">
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <strong className="fs-6 fw-bold">FirstName :</strong>
                    </div>
                    <div className="col-md-8 fs-6 ">{tenantDetails?.firstName}</div>
                  </div>

                  {/* <div className="row mb-3 mt-4">
                              <div className="col-md-4">
                                <strong className="fs-6 fw-bold">LastName :</strong>
                              </div>
                              <div className="col-md-8 fs-6 ">{tenantDetails?.lastName}</div>
                            </div> */}

                  <div className="row mb-3  mt-4">
                    <div className="col-md-4">
                      <strong className="fs-6 fw-bold">Email :</strong>
                    </div>
                    <div className="col-md-8 fs-6 ">{tenantDetails?.email}</div>
                  </div>

                  <div className="row mb-3   mt-4">
                    <div className="col-md-4">
                      <strong className="fs-6 fw-bold">Admin :</strong>
                    </div>
                    <div className="col-md-8 fs-6 ">
                      {tenantDetails?.isAdmin}
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="row mb-3 align-items-center">
                    <div className="col-md-4">
                      <strong className="fs-6 fw-bold">Company :</strong>
                    </div>
                    <div className="col-md-8 fs-6">
                      {tenantDetails?.company}
                    </div>
                  </div>

                  <div className="row mb-3 mt-4 align-items-center">
                    <div className="col-md-4">
                      <strong className="fs-6 fw-bold">Domain:</strong>
                    </div>
                    <div className="col-md-8 fs-6">
                      {tenantDetails?.domain}
                    </div>
                  </div>

                  {/* <div className="row mb-3 mt-4 align-items-center">
                              <div className="col-md-4">
                                <strong className="fs-6 fw-bold">Status :</strong>
                              </div>
                              <div className="col-md-8 fs-6">
                                {ticketDetails.status || "open"}
                              </div>
                            </div> */}

                  {/* <div className="row mb-3 mt-4 align-items-center">
                              <div className="col-md-4">
                                <strong className="fs-6 fw-bold">Image:</strong>
                              </div>
                              <div className="col-md-8 fs-6">
                                {ticketDetails.status || "open"}
                              </div>

                            </div> */}
                </div>
              </div>

              <div className="mt-4">
                <h6 className="fw-bold mb-3">Stripe (tenant billing)</h6>
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Stripe customer
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>
                          {stripeLoading ? "Loading…" : stripeSummary?.stripeCustomerId || "Not linked"}
                        </div>
                        {stripeError && (
                          <div className="text-danger" style={{ fontSize: 12 }}>
                            {stripeError}
                          </div>
                        )}
                      </div>
                      <button
                        className="btn btn-sm text-white"
                        style={{ backgroundColor: "#A1368B" }}
                        onClick={fetchStripeSummary}
                        disabled={stripeLoading}
                      >
                        Refresh
                      </button>
                    </div>

                    {stripeSummary?.stripeCustomerId && (
                      <div className="mt-3">
                        <div className="row">
                          <div className="col-md-4 mb-2">
                            <div className="text-muted" style={{ fontSize: 12 }}>Invoices (latest)</div>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>
                              {(stripeSummary?.invoices || []).length}
                            </div>
                          </div>
                          <div className="col-md-4 mb-2">
                            <div className="text-muted" style={{ fontSize: 12 }}>Payments (latest)</div>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>
                              {(stripeSummary?.paymentIntents || []).length}
                            </div>
                          </div>
                          <div className="col-md-4 mb-2">
                            <div className="text-muted" style={{ fontSize: 12 }}>Subscriptions</div>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>
                              {(stripeSummary?.subscriptions || []).length}
                            </div>
                          </div>
                        </div>

                        <div className="row mt-2">
                          <div className="col-lg-6 mb-3">
                            <div className="table-responsive">
                              <table className="table table-striped table-bordered mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th colSpan="3">Invoices (latest 5)</th>
                                  </tr>
                                  <tr>
                                    <th>Invoice</th>
                                    <th>Status</th>
                                    <th>Amount due</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(stripeSummary?.invoices || []).slice(0, 5).map((inv) => (
                                    <tr key={inv.id}>
                                      <td>{inv.number || inv.id}</td>
                                      <td>{inv.status}</td>
                                      <td>{typeof inv.amount_due === "number" ? (inv.amount_due / 100).toFixed(2) : "-"}</td>
                                    </tr>
                                  ))}
                                  {(stripeSummary?.invoices || []).length === 0 && (
                                    <tr>
                                      <td colSpan="3" className="text-center">No invoices</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="col-lg-6 mb-3">
                            <div className="table-responsive">
                              <table className="table table-striped table-bordered mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th colSpan="4">Payments (latest 5)</th>
                                  </tr>
                                  <tr>
                                    <th>PaymentIntent</th>
                                    <th>Status</th>
                                    <th>Amount</th>
                                    <th>Created</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(stripeSummary?.paymentIntents || []).slice(0, 5).map((pi) => (
                                    <tr key={pi.id}>
                                      <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {pi.id}
                                      </td>
                                      <td>{pi.status}</td>
                                      <td>
                                        {typeof pi.amount === "number"
                                          ? `${(pi.amount / 100).toFixed(2)} ${String(pi.currency || "").toUpperCase()}`
                                          : "-"}
                                      </td>
                                      <td>{pi.created ? new Date(pi.created * 1000).toISOString().slice(0, 10) : "-"}</td>
                                    </tr>
                                  ))}
                                  {(stripeSummary?.paymentIntents || []).length === 0 && (
                                    <tr>
                                      <td colSpan="4" className="text-center">No payments</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="col-12 mb-0">
                            <div className="table-responsive">
                              <table className="table table-striped table-bordered mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th colSpan="4">Subscriptions (latest 5)</th>
                                  </tr>
                                  <tr>
                                    <th>Subscription</th>
                                    <th>Status</th>
                                    <th>Current period end</th>
                                    <th>Cancel at period end</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(stripeSummary?.subscriptions || []).slice(0, 5).map((s) => (
                                    <tr key={s.id}>
                                      <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {s.id}
                                      </td>
                                      <td>{s.status}</td>
                                      <td>{s.current_period_end ? new Date(s.current_period_end * 1000).toISOString().slice(0, 10) : "-"}</td>
                                      <td>{String(!!s.cancel_at_period_end)}</td>
                                    </tr>
                                  ))}
                                  {(stripeSummary?.subscriptions || []).length === 0 && (
                                    <tr>
                                      <td colSpan="4" className="text-center">No subscriptions</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <h6 className="fw-bold mb-3">Stripe (full tenant report)</h6>
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Billing + refunds + Connect (payouts/balance/disputes)
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>
                          {stripeReportLoading ? "Loading…" : stripeReport?.generatedAt ? `Generated ${new Date(stripeReport.generatedAt).toISOString()}` : "—"}
                        </div>
                        {stripeReportError && (
                          <div className="text-danger" style={{ fontSize: 12 }}>
                            {stripeReportError}
                          </div>
                        )}
                      </div>
                      <button
                        className="btn btn-sm text-white"
                        style={{ backgroundColor: "#A1368B" }}
                        onClick={fetchStripeReport}
                        disabled={stripeReportLoading}
                      >
                        Refresh
                      </button>
                    </div>

                    {stripeReport?.billing?.stripeCustomerId && (
                      <div className="mt-3">
                        <div className="row">
                          <div className="col-md-3 mb-2">
                            <div className="text-muted" style={{ fontSize: 12 }}>Refunds (latest)</div>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>
                              {(stripeReport?.billing?.refunds || []).length}
                            </div>
                          </div>
                          <div className="col-md-3 mb-2">
                            <div className="text-muted" style={{ fontSize: 12 }}>Charges (latest)</div>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>
                              {(stripeReport?.billing?.charges || []).length}
                            </div>
                          </div>
                          <div className="col-md-3 mb-2">
                            <div className="text-muted" style={{ fontSize: 12 }}>Checkout sessions</div>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>
                              {(stripeReport?.billing?.checkoutSessions || []).length}
                            </div>
                          </div>
                          <div className="col-md-3 mb-2">
                            <div className="text-muted" style={{ fontSize: 12 }}>Invoices</div>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>
                              {(stripeReport?.billing?.invoices || []).length}
                            </div>
                          </div>
                        </div>

                        <div className="row mt-2">
                          <div className="col-lg-6 mb-3">
                            <div className="table-responsive">
                              <table className="table table-striped table-bordered mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th colSpan="4">Refunds (latest 10)</th>
                                  </tr>
                                  <tr>
                                    <th>Refund</th>
                                    <th>Status</th>
                                    <th>Amount</th>
                                    <th>Created</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(stripeReport?.billing?.refunds || []).slice(0, 10).map((r) => (
                                    <tr key={r.id}>
                                      <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{r.id}</td>
                                      <td>{r.status}</td>
                                      <td>{typeof r.amount === "number" ? `${(r.amount / 100).toFixed(2)} ${String(r.currency || "").toUpperCase()}` : "-"}</td>
                                      <td>{r.created ? new Date(r.created * 1000).toISOString().slice(0, 10) : "-"}</td>
                                    </tr>
                                  ))}
                                  {(stripeReport?.billing?.refunds || []).length === 0 && (
                                    <tr>
                                      <td colSpan="4" className="text-center">No refunds</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="col-lg-6 mb-3">
                            <div className="table-responsive">
                              <table className="table table-striped table-bordered mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th colSpan="4">Connect disputes (latest 10)</th>
                                  </tr>
                                  <tr>
                                    <th>Dispute</th>
                                    <th>Status</th>
                                    <th>Amount</th>
                                    <th>Created</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(stripeReport?.connect?.disputes || []).slice(0, 10).map((d) => (
                                    <tr key={d.id}>
                                      <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{d.id}</td>
                                      <td>{d.status}</td>
                                      <td>{typeof d.amount === "number" ? `${(d.amount / 100).toFixed(2)} ${String(d.currency || "").toUpperCase()}` : "-"}</td>
                                      <td>{d.created ? new Date(d.created * 1000).toISOString().slice(0, 10) : "-"}</td>
                                    </tr>
                                  ))}
                                  {(stripeReport?.connect?.disputes || []).length === 0 && (
                                    <tr>
                                      <td colSpan="4" className="text-center">
                                        {stripeReport?.connect?.connectedAccountId ? "No disputes" : "No connected account linked"}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-6 mb-0">
                            <div className="table-responsive">
                              <table className="table table-striped table-bordered mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th colSpan="4">Connect payouts (latest 10)</th>
                                  </tr>
                                  <tr>
                                    <th>Payout</th>
                                    <th>Status</th>
                                    <th>Amount</th>
                                    <th>Arrival</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(stripeReport?.connect?.payouts || []).slice(0, 10).map((p) => (
                                    <tr key={p.id}>
                                      <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{p.id}</td>
                                      <td>{p.status}</td>
                                      <td>{typeof p.amount === "number" ? `${(p.amount / 100).toFixed(2)} ${String(p.currency || "").toUpperCase()}` : "-"}</td>
                                      <td>{p.arrival_date ? new Date(p.arrival_date * 1000).toISOString().slice(0, 10) : "-"}</td>
                                    </tr>
                                  ))}
                                  {(stripeReport?.connect?.payouts || []).length === 0 && (
                                    <tr>
                                      <td colSpan="4" className="text-center">
                                        {stripeReport?.connect?.connectedAccountId ? "No payouts" : "No connected account linked"}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="col-lg-6 mb-0">
                            <div className="table-responsive">
                              <table className="table table-striped table-bordered mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th colSpan="4">Connect balance transactions (latest 10)</th>
                                  </tr>
                                  <tr>
                                    <th>Txn</th>
                                    <th>Type</th>
                                    <th>Net</th>
                                    <th>Created</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(stripeReport?.connect?.balanceTransactions || []).slice(0, 10).map((bt) => (
                                    <tr key={bt.id}>
                                      <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{bt.id}</td>
                                      <td>{bt.type}</td>
                                      <td>{typeof bt.net === "number" ? `${(bt.net / 100).toFixed(2)} ${String(bt.currency || "").toUpperCase()}` : "-"}</td>
                                      <td>{bt.created ? new Date(bt.created * 1000).toISOString().slice(0, 10) : "-"}</td>
                                    </tr>
                                  ))}
                                  {(stripeReport?.connect?.balanceTransactions || []).length === 0 && (
                                    <tr>
                                      <td colSpan="4" className="text-center">
                                        {stripeReport?.connect?.connectedAccountId ? "No balance transactions" : "No connected account linked"}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <h6 className="fw-bold mb-3">Stripe Meter usage (sms_units)</h6>
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Total units (latest {meterData?.events?.length || 0} events)
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 700 }}>
                          {meterLoading ? "…" : meterData?.totalUnits ?? 0}
                        </div>
                        {meterError && (
                          <div className="text-danger" style={{ fontSize: 12 }}>
                            {meterError}
                          </div>
                        )}
                      </div>
                      <button
                        className="btn btn-sm text-white"
                        style={{ backgroundColor: "#A1368B" }}
                        onClick={fetchStripeMeterEvents}
                        disabled={meterLoading}
                      >
                        Refresh
                      </button>
                    </div>

                    <div className="table-responsive mt-3">
                      <table className="table table-striped table-bordered mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th>Identifier</th>
                            <th>Units</th>
                            <th>Timestamp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(meterData?.events || []).slice(0, 10).map((ev) => (
                            <tr key={ev.identifier}>
                              <td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {ev.identifier}
                              </td>
                              <td>{ev.units}</td>
                              <td>{ev.timestamp ? new Date(ev.timestamp * 1000).toISOString() : "-"}</td>
                            </tr>
                          ))}
                          {!meterLoading && (meterData?.events || []).length === 0 && (
                            <tr>
                              <td colSpan="3" className="text-center">No meter events yet</td>
                            </tr>
                          )}
                          {meterLoading && (
                            <tr>
                              <td colSpan="3" className="text-center">Loading…</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <h6 className="fw-bold mb-3">SMS Credits</h6>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="card">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              Credits left
                            </div>
                            <div style={{ fontSize: 28, fontWeight: 700 }}>
                              {walletLoading ? "…" : balanceUnits}
                            </div>
                            {walletError && (
                              <div className="text-danger" style={{ fontSize: 12 }}>
                                Failed to load wallet balance
                              </div>
                            )}
                          </div>
                          <button
                            className="btn btn-sm text-white"
                            style={{ backgroundColor: "#A1368B" }}
                            onClick={() => refetchWallet()}
                            disabled={walletLoading}
                          >
                            Refresh
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <h6 className="fw-bold mb-3">SMS Usage (last 30 days)</h6>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="card">
                      <div className="card-body">
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Outbound sent ({usage?.fromDay || fromDay} →{" "}
                          {usage?.toDay || toDay})
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 700 }}>
                          {usageLoading ? "…" : usage?.totalOutboundSent ?? 0}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="card">
                      <div className="card-body">
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Inbound received ({usage?.fromDay || fromDay} →{" "}
                          {usage?.toDay || toDay})
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 700 }}>
                          {usageLoading ? "…" : usage?.totalInboundReceived ?? 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>Daily breakdown (UTC)</strong>
                      <button
                        className="btn btn-sm text-white"
                        style={{ backgroundColor: "#A1368B" }}
                        onClick={() => refetchUsage()}
                        disabled={usageLoading}
                      >
                        Refresh
                      </button>
                    </div>

                    {usageError && (
                      <div className="text-danger" style={{ fontSize: 12 }}>
                        Failed to load SMS usage details
                      </div>
                    )}

                    <div className="table-responsive">
                      <table className="table table-striped table-bordered mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th style={{ width: 150 }}>Day</th>
                            <th>Outbound</th>
                            <th>Inbound</th>
                          </tr>
                        </thead>
                        <tbody>
                          {!usageLoading &&
                          (usage?.daily?.length || 0) === 0 ? (
                            <tr>
                              <td colSpan="3" className="text-center">
                                No usage in this period
                              </td>
                            </tr>
                          ) : (
                            (usage?.daily || []).map((d) => (
                              <tr key={d.day}>
                                <td>{d.day}</td>
                                <td>{d.outboundSent}</td>
                                <td>{d.inboundReceived}</td>
                              </tr>
                            ))
                          )}
                          {usageLoading && (
                            <tr>
                              <td colSpan="3" className="text-center">
                                Loading…
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <h6 className="fw-bold mb-3 mt-4">Email Usage (last 30 days)</h6>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="card">
                      <div className="card-body">
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Outbound sent ({emailUsage?.fromDay || fromDay} →{" "}
                          {emailUsage?.toDay || toDay})
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 700 }}>
                          {emailUsageLoading ? "…" : emailUsage?.totalOutbound ?? 0}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="card">
                      <div className="card-body">
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Inbound received ({emailUsage?.fromDay || fromDay} →{" "}
                          {emailUsage?.toDay || toDay})
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 700 }}>
                          {emailUsageLoading ? "…" : emailUsage?.totalInbound ?? 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>Daily breakdown (UTC)</strong>
                      <button
                        className="btn btn-sm text-white"
                        style={{ backgroundColor: "#A1368B" }}
                        onClick={() => refetchEmailUsage()}
                        disabled={emailUsageLoading}
                      >
                        Refresh
                      </button>
                    </div>

                    {emailUsageError && (
                      <div className="text-danger" style={{ fontSize: 12 }}>
                        Failed to load email usage details
                      </div>
                    )}

                    <div className="table-responsive">
                      <table className="table table-striped table-bordered mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th style={{ width: 150 }}>Day</th>
                            <th>Outbound</th>
                            <th>Inbound</th>
                          </tr>
                        </thead>
                        <tbody>
                          {!emailUsageLoading &&
                          (emailUsage?.daily?.length || 0) === 0 ? (
                            <tr>
                              <td colSpan="3" className="text-center">
                                No usage in this period. Email usage tracking started recently.
                              </td>
                            </tr>
                          ) : (
                            (emailUsage?.daily || []).map((d) => (
                              <tr key={d.day}>
                                <td>{d.day}</td>
                                <td>{d.outboundUnits}</td>
                                <td>{d.inboundUnits}</td>
                              </tr>
                            ))
                          )}
                          {emailUsageLoading && (
                            <tr>
                              <td colSpan="3" className="text-center">
                                Loading…
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex mt-4 justify-content-end">

                <button
                  className="btn  text-white"
                  style={{ backgroundColor: "#A1368B" }}
                  onClick={handleNavigateSupport}
                >
                  BACK
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TenantDetails;
